import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download } from 'lucide-react';
import * as THREE from 'three';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { IFCLoader } from "web-ifc-three";


const ProjectDetailsPage = () => {
  // Sample PDF list
  const pdfFiles = [
    { id: 1, name: 'Architectural Plan', path: '/pdfs/architectural-plan.pdf' },
    { id: 2, name: 'Structural Drawings', path: '/pdfs/structural-drawings.pdf' },
    { id: 3, name: 'Electrical Layout', path: '/pdfs/electrical-layout.pdf' },
    { id: 4, name: 'Plumbing Schematic', path: '/pdfs/plumbing-schematic.pdf' }
  ];

  // Sample IFC list
  const ifcFiles = [
    { id: 1, name: 'Main Building Structure', path: '/AdvancedProject.ifc' },
    { id: 2, name: 'Electrical Systems', path: '/AdvancedProject.ifc' },
    { id: 3, name: 'HVAC Layout', path: '/AdvancedProject.ifc' }
  ];

  const [activeFile, setActiveFile] = useState('pdf');
  const [selectedPdf, setSelectedPdf] = useState(pdfFiles[0]);
  const [selectedIfc, setSelectedIfc] = useState(ifcFiles[0]);
  const ifcViewerContainerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const ifcLoaderRef = useRef(null);

  // Initialize IFC Viewer
  useEffect(() => {
    if (activeFile !== '3d' || !ifcViewerContainerRef.current) return;

    // Clear previous scene
    if (sceneRef.current) {
      while (sceneRef.current.children.length > 0) {
        sceneRef.current.remove(sceneRef.current.children[0]);
      }
    }

    /** ✅ Setup Scene */
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;

    /** ✅ Setup Camera */
    const size = {
      width: ifcViewerContainerRef.current.clientWidth,
      height: ifcViewerContainerRef.current.clientHeight,
    };
    const camera = new THREE.PerspectiveCamera(75, size.width / size.height, 0.1, 1000);
    camera.position.set(10, 10, 10);
    cameraRef.current = camera;

    /** ✅ Setup Renderer */
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(size.width, size.height);
    renderer.setPixelRatio(window.devicePixelRatio);
    ifcViewerContainerRef.current.innerHTML = '';
    ifcViewerContainerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    /** ✅ Setup Lighting */
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 20, 10);
    scene.add(directionalLight);

    /** ✅ Setup Grid and Axes */
    const grid = new THREE.GridHelper(50, 50);
    scene.add(grid);
    const axes = new THREE.AxesHelper(10);
    scene.add(axes);

    /** ✅ Setup OrbitControls */
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    /** ✅ Setup IFC Loader */
    if (!ifcLoaderRef.current) {
      ifcLoaderRef.current = new IFCLoader();
      ifcLoaderRef.current.ifcManager.setWasmPath("/");
    }

    /** ✅ Load IFC Model */
    const loadIFCModel = async () => {
      if (!selectedIfc) return;
      
      try {
        const ifcModel = await ifcLoaderRef.current.loadAsync(selectedIfc.path);

        if (!ifcModel) {
          console.error("Failed to load IFC model.");
          return;
        }

        scene.add(ifcModel);

        /** ✅ Adjust Camera to Fit Model */
        const box = new THREE.Box3().setFromObject(ifcModel);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        const distance = maxDim / (2 * Math.tan(fov / 2));

        camera.position.set(center.x + distance, center.y + distance, center.z + distance);
        camera.lookAt(center);

        /** ✅ Fix Model Floating Issue */
        const minY = box.min.y; // Get lowest Y point of the model
        ifcModel.position.y -= minY; // Shift model up/down so lowest point is at y=0

      } catch (error) {
        console.error("Error loading IFC file:", error);
      }
    };

    loadIFCModel();

    /** ✅ Render Loop */
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    /** ✅ Handle Resize */
    const handleResize = () => {
      size.width = ifcViewerContainerRef.current.clientWidth;
      size.height = ifcViewerContainerRef.current.clientHeight;
      camera.aspect = size.width / size.height;
      camera.updateProjectionMatrix();
      renderer.setSize(size.width, size.height);
    };
    window.addEventListener("resize", handleResize);

    /** ✅ Cleanup */
    return () => {
      window.removeEventListener("resize", handleResize);
      renderer.dispose();
    };

  }, [activeFile, selectedIfc]);

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commercial Complex - Downtown</h1>
          <p className="text-gray-600">Client: ABC Developers</p>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Main Content Area */}
        <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
          
          
          {/* File Type Switcher */}
          <div className="flex space-x-4 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveFile('pdf')}
              className={`pb-4 px-2 flex items-center ${activeFile === 'pdf' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            >
              <FileText className="mr-2 h-4 w-4" /> PDF Documents
            </button>
            <button
              onClick={() => setActiveFile('3d')}
              className={`pb-4 px-2 flex items-center ${activeFile === '3d' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            >
              3D Model
            </button>
          </div>
          <div style={{ position: 'relative', width: '100%', height: '70vh' }}>
          {/* PDF Section */}
          {activeFile === 'pdf' && (
            <div className="flex">
               <div className="w-64 pr-4 border-r border-gray-200">
                <h3 className="font-semibold mb-4 text-gray-700">PDF FILES</h3>
                <ul className="space-y-2">
                  {pdfFiles.map((pdf) => (
                    <li key={pdf.id} onClick={() => setSelectedPdf(pdf)} className={`cursor-pointer p-2 rounded ${selectedPdf.id === pdf.id ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>
                      <span className="truncate mr-2">{pdf.name}</span>
                      <a href={pdf.path} download onClick={(e) => e.stopPropagation()} className="text-gray-500 hover:text-blue-600">
                        <Download className="h-4 w-4" />
                      </a>
                    </li>
                  ))}
                </ul>
              
                 
              </div>
              <iframe src={selectedPdf.path} width="100%" height="500px" className="rounded-lg"></iframe>
            </div>
          )}

          {/* 3D Model Section */}
          {activeFile === '3d' && (
            <div className="flex">
              <div className="w-64 pr-4 border-r border-gray-200">
                <h3 className="font-semibold mb-4 text-gray-700">3D Models</h3>
                <ul>
                  {ifcFiles.map((ifc) => (
                    <li key={ifc.id} onClick={() => setSelectedIfc(ifc)} className={`cursor-pointer p-2 rounded ${selectedIfc.id === ifc.id ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>
                      {ifc.name}
                    </li>
                  ))}
                </ul>
              </div>
               
              <div style={{width: '100%', height: '70vh'}} className="flex-1 pl-4" ref={ifcViewerContainerRef} />
           
            </div>
          )}
          </div>

        </div>
      </div>
      
    </div>
  );
};

export default ProjectDetailsPage;
 